import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, FileText, Loader2 } from "lucide-react";
import { DocumentLibrary, openDocumentFile, describeOpenError } from "@/components/library/DocumentLibrary";
import { useFocusScroll } from "@/lib/navigation";

// V4.6 Dealer Technical Library (MEDIA-151/153/155/156; CMD-11-007…009):
// published controlled records + released documents only — drafts, internal
// notes and admin metadata never reach this surface.
export default function DealerKnowledge() {
  const [records, setRecords] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [detail, setDetail] = useState(null);
  const [unavailable, setUnavailable] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [evidenceDetail, setEvidenceDetail] = useState(null);
  const fromCopilot = params.get("from") === "copilot";
  const sourceRequested = params.get("source") === "1";
  const focusDoc = params.get("document");  // V5-D NAV-002 / TLIB-003: exact released document
  const focusRev = params.get("revision");  // V5-H3 ATT-007: exact released revision
  const focusDocRow = focusDoc ? documents.find((d) => d.doc_number === focusDoc) : null;
  const focusDocHit = !!(focusDocRow && (!focusRev || String(focusDocRow.revision) === focusRev));
  useFocusScroll(focusDocHit ? `library-doc-${focusDoc}` : null, focusDocHit);

  const load = useCallback(async (query = "", kindFilter = "all") => {
    try {
      const { data } = await api.get("/knowledge/records", {
        params: { ...(query.trim() ? { q: query.trim() } : {}), ...(kindFilter !== "all" ? { kind: kindFilter } : {}) },
      });
      setRecords(data.records || []);
    } catch (e) {
      if (e.response?.status === 404) setRecords([]);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
      if (e.response?.status !== 404) setRecords([]);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/knowledge/documents").then((r) => setDocuments(r.data.documents || [])).catch(() => {});
  }, [load]);

  // Deep-link contract (COP-KNOW-158 / CMD-11-011/012): open the exact authorized
  // record, or show an explicit unavailable state — never a silent fallback.
  useEffect(() => {
    const target = params.get("record");
    const ev = params.get("evidence");
    if (ev) {
      // RC48-COP-003: an evidence card opens the exact evidence record
      api.get("/knowledge/evidence").then(({ data }) => {
        const hit = (data.evidence || []).find((e) => e.evidence_id === ev);
        if (hit) { setEvidenceDetail(hit); setUnavailable(null); } else { setEvidenceDetail(null); setUnavailable(ev); }
      }).catch(() => { setEvidenceDetail(null); setUnavailable(ev); });
      return;
    }
    if (!target) return;
    (async () => {
      try {
        const { data } = await api.get(`/knowledge/records/${encodeURIComponent(target)}`);
        setDetail(data.record);
        setUnavailable(null);
      } catch {
        setDetail(null);
        setUnavailable(target);
      }
    })();
  }, [params]);

  const open = async (kid) => {
    setParams({ record: kid });
  };
  // RC48-COP-015: Back/Close returns to the originating Copilot conversation
  const close = () => {
    setDetail(null); setUnavailable(null); setEvidenceDetail(null);
    if (fromCopilot) navigate(-1); else setParams({});
  };

  const kinds = useMemo(() => [...new Set((records || []).map((r) => r.kind).filter(Boolean))], [records]);

  const search = (value, k = kind) => { setQ(value); load(value, k); };

  return (
    <div data-testid="dealer-library-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Zelligio Connect</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Technical Library</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Approved Zelligio technical records and released controlled documents. Every record carries its source,
        revision, exact locator, scope, limitations and evidence status.
      </p>

      <div className="mt-8" data-testid="library-quick-answers">
        <h2 className="font-display text-2xl font-light text-charcoal">Quick Answers</h2>
        <p className="mt-1 text-xs text-warmgrey">Governed, high-frequency technical answers. Open a card for the full record, source and limitations.</p>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search topic, product, application…"
          data-testid="library-search" className="h-11 w-full bg-white focus-visible:ring-terracotta sm:w-80" />
        <Select value={kind} onValueChange={(v) => { setKind(v); search(q, v); }}>
          <SelectTrigger data-testid="library-kind-filter" className="h-11 w-full bg-white sm:w-56"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All topics</SelectItem>
            {kinds.map((k) => <SelectItem key={k} value={k}>{k.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {records === null && <p className="mt-8 text-sm text-warmgrey" data-testid="library-loading"><Loader2 className="h-4 w-4 animate-spin" /></p>}
      {records?.length === 0 && (
        <p className="mt-8 rounded-md border border-dashed border-limestone p-6 text-sm text-warmgrey" data-testid="library-empty">
          No published records match. Try a narrower or different search.
        </p>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {(records || []).map((r) => (
          <button key={r.knowledge_id} type="button" onClick={() => open(r.knowledge_id)}
            className="rounded-md border border-border bg-white p-4 text-left transition-colors hover:border-terracotta"
            data-testid={`library-record-${r.knowledge_id}`}>
            <p className="flex items-center gap-2 text-sm font-medium text-charcoal">
              <BookOpen className="h-4 w-4 shrink-0 text-terracotta" />{r.topic}
            </p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-warmgrey">
              {(r.kind || "").replaceAll("_", " ")} · {r.knowledge_id} · {r.verification_state || "verification pending"}
            </p>
          </button>
        ))}
      </div>

      <div className="mt-12 border-t border-limestone pt-8" id="documents">
        <h2 className="font-display text-2xl font-light text-charcoal">Document Library</h2>
        <p className="mt-1 text-xs text-warmgrey">Released technical files — specifications, installation guidance, testing and compliance, care, packaging.</p>
        {focusDoc && documents.length > 0 && !focusDocHit && (
          <p className="mt-3 rounded-md border border-dashed border-limestone bg-white p-4 text-sm text-warmgrey" data-testid="library-document-unavailable">
            {focusDocRow && focusRev
              ? <>Revision <span className="font-mono font-semibold">{focusRev}</span> of <span className="font-mono font-semibold">{focusDoc}</span> is not the released revision — the current released revision is Rev {focusDocRow.revision}.</>
              : <>The document <span className="font-mono font-semibold">{focusDoc}</span> is not available to your role or is no longer released.</>}
          </p>
        )}
        <DocumentLibrary documents={documents} focusDoc={focusDoc} />
      </div>

      <Dialog open={!!detail || !!unavailable || !!evidenceDetail} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-white" data-testid="library-record-detail">
          {unavailable ? (
            <div data-testid="library-unavailable">
              <DialogHeader><DialogTitle className="font-display text-2xl font-light">Record unavailable</DialogTitle></DialogHeader>
              <p className="mt-2 text-sm text-warmgrey">
                The record <span className="font-mono font-semibold">{unavailable}</span> is not available to your role,
                is not published, or no longer exists. Nothing was opened.
              </p>
            </div>
          ) : evidenceDetail ? (
            <div data-testid="library-evidence-detail">
              <DialogHeader><DialogTitle className="font-display text-2xl font-light">{evidenceDetail.technical_area}</DialogTitle></DialogHeader>
              <p className="mt-1 text-[11px] uppercase tracking-wide text-warmgrey">evidence record · {evidenceDetail.evidence_id}</p>
              <div className="mt-4 space-y-2 text-sm text-charcoal">
                <p><span className="font-medium">Current documented status:</span> {evidenceDetail.current_documented_status || "Not stated"}</p>
                <p><span className="font-medium">What may be communicated:</span> {evidenceDetail.what_may_be_communicated}</p>
                <p data-testid="library-evidence-report">
                  <span className="font-medium">Independent report:</span> {evidenceDetail.report_available ? "registered" : "No independent report is registered for this evidence record."}
                </p>
                {evidenceDetail.limitations && <p className="rounded-sm bg-amber-50 p-2 text-xs text-amber-800">Limitations: {evidenceDetail.limitations}</p>}
              </div>
            </div>
          ) : detail && (
            <div>
              <DialogHeader><DialogTitle className="font-display text-2xl font-light">{detail.topic}</DialogTitle></DialogHeader>
              <p className="mt-1 text-[11px] uppercase tracking-wide text-warmgrey" data-testid="library-detail-kind">
                {(detail.kind || "").replaceAll("_", " ")} · {detail.knowledge_id} · {detail.publication_status || "published"} · Rev {detail.revision ?? "—"}
              </p>
              <div className="mt-4 space-y-2 text-sm text-charcoal" data-testid="library-detail-content">
                {Object.entries(detail.content || {}).map(([k, v]) => (
                  <p key={k}><span className="font-medium capitalize">{k.replaceAll("_", " ")}:</span> {String(v)}</p>
                ))}
              </div>
              {detail.limitations && (
                <p className="mt-3 rounded-sm bg-amber-50 p-2 text-xs text-amber-800" data-testid="library-detail-limitations">
                  Limitations: {detail.limitations}
                </p>
              )}
              <div className="mt-4 rounded-md border border-border bg-sand p-3 text-xs text-charcoal" data-testid="library-citation">
                <p className="font-semibold uppercase tracking-widest text-warmgrey">Source &amp; evidence</p>
                <p className="mt-1" data-testid="library-citation-record">Record: {detail.knowledge_id}</p>
                <p data-testid="library-citation-source">
                  Source: {detail.source?.workbook || detail.primary_source || "—"} · Rev {detail.source?.revision || "—"}
                  {detail.source?.sheet ? ` · ${detail.source.sheet}${detail.source?.row ? ` row ${detail.source.row}` : ""}` : ""}
                </p>
                <p data-testid="library-citation-scope">Scope: {detail.applicability_scope || "As stated in the record"}</p>
                <p data-testid="library-citation-evidence">Evidence status: {detail.evidence_status || "Not stated"}</p>
                {detail.effective_date && <p>Effective: {detail.effective_date}</p>}
                {(detail.variant_skus?.length > 0 || detail.color_codes?.length > 0) && (
                  <p data-testid="library-citation-products">
                    Applies to: {[...(detail.variant_skus || []), ...(detail.color_codes || [])].join(", ")}
                  </p>
                )}
                {(detail.provenance || []).length > 0 && (
                  <p data-testid="library-citation-provenance">
                    Provenance: {detail.provenance.map((h) => `${h.action} rev ${h.revision} by ${h.actor} (${String(h.at || "").slice(0, 10)})`).join(" → ")}
                  </p>
                )}
                {detail.approved_by && <p>Approved by: {detail.approved_by}</p>}
              </div>
              {/* RC48-COP-012/013: functional source-document action or an explicit statement */}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {detail.source_document ? (
                  <button type="button" onClick={async () => { try { await openDocumentFile(detail.source_document); } catch (e) { toast.error(describeOpenError(e)); } }}
                    className={`flex items-center gap-1.5 rounded-full border border-terracotta px-3 py-1 text-xs font-medium text-terracotta transition-colors hover:bg-terracotta hover:text-white ${sourceRequested ? "ring-2 ring-terracotta/40" : ""}`}
                    data-testid="library-open-source-document">
                    <FileText className="h-3.5 w-3.5" />Open Source Document ({detail.source_document})
                  </button>
                ) : (
                  <p className="text-xs text-warmgrey" data-testid="library-no-source-document">
                    No source document is registered for this record — the controlled workbook {detail.source?.workbook || "—"} Rev {detail.source?.revision || "—"} is the cited source.
                  </p>
                )}
                {fromCopilot && (
                  <button type="button" onClick={close} className="text-xs font-medium text-terracotta hover:underline" data-testid="library-back-to-copilot">
                    ← Back to ZELI conversation
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
